import React, { useState, useEffect } from 'react';
import { App } from './App';
import { QuotationReview } from './QuotationReview';
import { ControlCenter } from './ControlCenter';
import { LandingPage } from './LandingPage';
import { getApiUrl } from '@/config';

type Route = 'landing' | 'customer' | 'admin' | 'review';

interface RouterProps {}

export const Router: React.FC<RouterProps> = () => {
  const [currentRoute, setCurrentRoute] = useState<Route>('landing');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Parse URL to determine route
    const path = window.location.pathname;
    const hash = window.location.hash;

    if (path === '/admin' || hash === '#/admin') {
      setCurrentRoute('admin');
    } else if (path === '/review' || hash === '#/review') {
      setCurrentRoute('review');
    } else if (path === '/chat' || path === '/customer' || hash === '#/chat' || hash === '#/customer') {
      setCurrentRoute('customer');
    } else {
      // Default to landing page for root path
      setCurrentRoute('landing');
    }

    // Check authentication for admin routes
    if ((path === '/admin' || path === '/review' || hash === '#/admin' || hash === '#/review')) {
      checkAuthentication();
    }
  }, []);

  const checkAuthentication = () => {
    // Check for existing session
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
      // Verify session with backend
      fetch(getApiUrl('/api/auth/validate-session?session_token=' + sessionToken))
        .then(response => {
          if (response.ok) {
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('session_token');
            setIsAuthenticated(false);
            showLoginPrompt();
          }
        })
        .catch(() => {
          setIsAuthenticated(false);
          showLoginPrompt();
        });
    } else {
      showLoginPrompt();
    }
  };

  const showLoginPrompt = () => {
    const username = prompt('Enter admin username:');
    const password = prompt('Enter admin password:');

    if (username && password) {
      fetch(getApiUrl('/api/auth/login-step1'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.session_token) {
          localStorage.setItem('session_token', data.session_token);
          setIsAuthenticated(true);
        } else {
          alert('Authentication failed');
          navigateToCustomerLocal();
        }
      })
      .catch(() => {
        alert('Authentication error');
        navigateToCustomerLocal();
      });
    } else {
      navigateToCustomerLocal();
    }
  };

  const navigateToCustomerLocal = () => {
    window.location.href = '/';
    setCurrentRoute('landing');
  };

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    setIsAuthenticated(false);
    navigateToCustomerLocal();
  };

  const handleNavigateToChat = () => {
    window.history.pushState({}, '', '/chat');
    setCurrentRoute('customer');
  };

  const handleNavigateToLanding = () => {
    window.history.pushState({}, '', '/');
    setCurrentRoute('landing');
  };

  // Render based on current route
  if (currentRoute === 'landing') {
    return <LandingPage onNavigateToChat={handleNavigateToChat} />;
  }

  if (currentRoute === 'admin' && isAuthenticated) {
    return <ControlCenter onBack={handleNavigateToLanding} onLogout={handleLogout} />;
  }

  if (currentRoute === 'review' && isAuthenticated) {
    return <QuotationReview onBack={handleNavigateToLanding} />;
  }

  // Customer chat interface
  return <App />;
};

// Helper functions for navigation
export const navigateToAdmin = () => {
  window.location.href = '/admin';
};

export const navigateToReview = () => {
  window.location.href = '/review';
};

export const navigateToChat = () => {
  window.location.href = '/chat';
};

export const navigateToLanding = () => {
  window.location.href = '/';
};

// Deprecated: use navigateToChat or navigateToLanding instead
export const navigateToCustomer = navigateToChat;